import * as Permissions from "@/constants/permissions";
import * as Routes from "@/constants/routes";
import _ from "lodash";

// Factory function for dependency injection
export function createRoutesUtil({ router, store, auth_util, routes }) {
    return {
        /**
         *  Returning an array of routes that are accessible to the user.
         *
         * @returns {Array}
         */
        accessibleRoutes() {
            let user = store.state.user;
            let accessible = [Routes.TERMS, Routes.HOME];
            if (user) {
                let calendar_routes = [
                    Routes.CREATE_CALENDAR,
                    Routes.EDIT_CALENDAR
                ];
                let newsletter_routes = [Routes.EDIT_NEWSLETTER];
                [Routes.HOME, Routes.ACCOUNT_DETAILS].forEach((item) =>
                    accessible.push(item)
                );
                if (store.state.current_community) {
                    let community = store.state.current_community;
                    if (community.has_calendar) {
                        calendar_routes.forEach((item) =>
                            accessible.push(item)
                        );
                    }
                    if (community.has_newsletter) {
                        newsletter_routes.forEach((item) =>
                            accessible.push(item)
                        );
                    }
                    [
                        Routes.DASHBOARD,
                        Routes.ORDERS,
                        Routes.VIEW_ORDER,
                        Routes.CART,
                        Routes.CHECKOUT,
                        Routes.SUSPENDED,
                        Routes.EVENT_LOGS,
                        Routes.NEWS_PROOFS
                    ].forEach((i) => accessible.push(i));
                } else if (store.state.current_corporation) {
                    [
                        Routes.COMMUNITIES,
                        Routes.USERS,
                        Routes.ORDERS,
                        Routes.ANNOUNCEMENTS
                    ].forEach((i) => accessible.push(i));
                } else {
                    // backend routes
                    calendar_routes.forEach((i) => accessible.push(i));
                    newsletter_routes.forEach((i) => accessible.push(i));
                    let permissionedRoutes = {
                        [Routes.ORDER_DASHBOARD]:
                            Permissions.VIEW_PENDING_ORDERS,
                        [Routes.CORPORATIONS]: Permissions.VIEW_CORPORATIONS,
                        [Routes.CREATE_CORPORATION]:
                            Permissions.CREATE_CORPORATION,
                        [Routes.EDIT_CORPORATION]: Permissions.EDIT_CORPORATION,
                        [Routes.COMMUNITIES]: Permissions.VIEW_COMMUNITIES,
                        [Routes.CREATE_COMMUNITY]: Permissions.CREATE_COMMUNITY,
                        [Routes.EDIT_COMMUNITY]: Permissions.EDIT_COMMUNITY,
                        [Routes.EDIT_ORDER_INVOICE_LINES]:
                            Permissions.EDIT_INVOICE,
                        [Routes.USERS]: Permissions.VIEW_USERS,
                        [Routes.THEMES]: Permissions.VIEW_THEMES,
                        [Routes.PRICING]: Permissions.VIEW_PRICING,
                        [Routes.SETTINGS]: Permissions.VIEW_SHIPPING,
                        [Routes.EARLY_BIRD]: Permissions.VIEW_EARLY_BIRD,
                        [Routes.SAGE]: Permissions.VIEW_SAGE,
                        [Routes.PENDING_ORDERS]:
                            Permissions.VIEW_PENDING_ORDERS,
                        [Routes.ORDER_HUB]: Permissions.VIEW_PENDING_ORDERS,
                        [Routes.CLOSED_ORDERS]: Permissions.VIEW_CLOSED_ORDERS,
                        [Routes.EDIT_ORDER]: Permissions.EDIT_ORDER,
                        [Routes.EDIT_ORDER_STATUS]:
                            Permissions.CHECK_ORDER_STATUS,
                        [Routes.VIEW_ORDER]: Permissions.VIEW_ORDER_DETAILS,
                        [Routes.FILLER_ARTICLES]:
                            Permissions.VIEW_FILLER_ARTICLES,
                        [Routes.CREATE_FILLER_ARTICLE]:
                            Permissions.UPDATE_FILLER_ARTICLES,
                        [Routes.EDIT_FILLER_ARTICLE]:
                            Permissions.UPDATE_FILLER_ARTICLES,
                        [Routes.NOTIFICATION_SETTINGS]:
                            Permissions.VIEW_NOTIFICATIONS,
                        [Routes.HOLIDAY]: Permissions.VIEW_HOLIDAY,
                        [Routes.COMMUNITY_LOGS]: Permissions.COMMUNITY_LOGS
                    };
                    _.forEach(permissionedRoutes, (permission, route) => {
                        if (auth_util.can(permission)) {
                            accessible.push(route);
                        }
                    });
                }
            } else {
                [
                    Routes.FORGOT_PASSWORD,
                    Routes.RESET_PASSWORD,
                    Routes.LOGIN,
                    Routes.REGISTER
                ].forEach((item) => accessible.push(item));
            }
            return accessible.filter((a) => !!a);
        },
        /* Checking if the route is accessible to the user. */
        isRouteAccessible(name) {
            return this.accessibleRoutes().includes(name);
        },

        /**
         * Check if the user has permission to access a specific route
         * based on both route accessibility and role requirements
         *
         * @param {String} routeName - The name of the route to check
         * @return {Boolean} - Whether the user has permission
         */
        hasRoutePermission(routeName) {
            // First check if the route is accessible
            if (!this.isRouteAccessible(routeName)) {
                return false;
            }

            // Then check if the user has the required role
            const routeObj = router.options.routes
                .flatMap((r) => r.children || [r])
                .find((r) => r.name === routeName);

            if (routeObj && routeObj.meta && routeObj.meta.roles) {
                const requiredRoles = routeObj.meta.roles;
                return auth_util.hasRole(requiredRoles);
            }

            // No role restrictions
            return true;
        },

        /**
         * Checking if the route exists in the routes array.
         *
         * @param {String} name
         * @return {Boolean}
         */
        routeExists(name) {
            return !!_.find(routes, (item) => {
                if (item.name === name) {
                    return true;
                }
                if (item.children) {
                    return !!_.find(item.children, (c) => c.name === name);
                }
                return false;
            });
        },

        /**
         * Safely navigate to a route, avoiding NavigationDuplicated errors
         * @param {Object} route - Route object with name and optional params/query
         * @return {Promise}
         */
        safeNavigate(route) {
            // Check if we're already on the target route
            if (
                router.currentRoute &&
                router.currentRoute.name === route.name
            ) {
                // If params and query are the same, don't navigate
                const currentParams = router.currentRoute.params || {};
                const currentQuery = router.currentRoute.query || {};
                const targetParams = route.params || {};
                const targetQuery = route.query || {};

                if (
                    _.isEqual(currentParams, targetParams) &&
                    _.isEqual(currentQuery, targetQuery)
                ) {
                    return Promise.resolve();
                }
            }

            return router.push(route).catch((err) => {
                // Ignore NavigationDuplicated errors
                if (err.name !== "NavigationDuplicated") {
                    throw err;
                }
            });
        }
    };
}
